import { Packet } from "../Interfaces/commonDtos";
import { DropdownItem } from "../Interfaces/dropdownInterfaces";
import {
  IAddEditMarketmakingTask,
  IMarketmakingConfig,
  IMarketmakingOrderbookPairResp,
  IMarketmakingOrderTableReport,
  IMarketmakingTradeTableSearchParams,
  IMarketmakingProgram,
  IMarketmakingTaskSearchParam,
  IMarketmakingTaskTableReport,
  IMarketmakingTradeTableReport,
  IMarketmakingTriggerTableReport,
  IMarketmakingSetProgramBody,
  IMarketmakingOrderTableSearchParams,
  IGetPreviewOfferResp,
  IMarketmakingPredefinedProgram,
  IFirstRowDelta,
  IManualConfigDTO,
} from "../Interfaces/deltaHedgeMarketmaking";
import { ErrorTableReport } from "../Interfaces/orderInterfaces";
import { Report } from "../Interfaces/TableInterfaces";
import {
  deleteRequest,
  getRequest,
  getTable,
  patchRequest,
  postRequest,
  putRequest,
} from "./requestTemplates";
import { AxiosRequestConfig } from "axios";

export const getMarketmakingTasksTable = (
  searchParam: IMarketmakingTaskSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig
): Promise<Report<IMarketmakingTaskTableReport>> =>
  getTable<IMarketmakingTaskSearchParam, IMarketmakingTaskTableReport>(
    "/v1/deltaHedgeMarketMaking/taskTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions
  );

export const getMarketmakingOrderbookPair = (
  taskId: string
): Promise<IMarketmakingOrderbookPairResp> => {
  const urlSearchParam = new URLSearchParams();
  let urlStr = "/v1/deltaHedgeMarketMaking/GetOrderBook";
  if (taskId) {
    urlSearchParam.append("TaskId", taskId.toString());
    urlStr += "?" + urlSearchParam;
  }
  return getRequest(urlStr);
};

export const addMarketmakingTask = (
  formValues: IAddEditMarketmakingTask
): Promise<Packet<boolean>> =>
  postRequest("/v1/deltaHedgeMarketMaking/addTask", formValues);

export const editMarketmakingTask = (
  formValues: IAddEditMarketmakingTask
): Promise<Packet<boolean>> =>
  putRequest("/v1/deltaHedgeMarketMaking/editTask", formValues);

export const getMarketmakingOrderTable = (
  searchParam: IMarketmakingOrderTableSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig
): Promise<Report<IMarketmakingOrderTableReport>> =>
  getTable<IMarketmakingOrderTableSearchParams, IMarketmakingOrderTableReport>(
    "/v1/deltaHedgeMarketMaking/OrderTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions
  );

export const getReferenceTable = (
  searchParam: any,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig
): Promise<Report<any>> =>
  getTable<any, any>(
    "/v1/deltaHedgeMarketMaking/getRefrenceTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions
  );

export const getMarketmakingTradeTable = (
  searchParam: IMarketmakingTradeTableSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig
): Promise<Report<IMarketmakingTradeTableReport>> =>
  getTable<IMarketmakingTradeTableSearchParams, IMarketmakingTradeTableReport>(
    "/v1/deltaHedgeMarketMaking/TradeTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions
  );

export const getMarketmakingErrorsTable = (
  searchParam: IMarketmakingTradeTableSearchParams,
  offset: number | null,
  pageSize: number | null
): Promise<Report<ErrorTableReport>> =>
  getTable<IMarketmakingTradeTableSearchParams, ErrorTableReport>(
    "/v1/deltaHedgeMarketMaking/ErrorTable",
    searchParam,
    offset,
    pageSize
  );

export const getMarketmakingTriggerTable = (
  searchParam: { taskId: number },
  offset: number | null,
  pageSize: number | null
): Promise<Report<IMarketmakingTriggerTableReport>> =>
  getTable<{ taskId: number }, IMarketmakingTriggerTableReport>(
    "/v1/deltaHedgeMarketMaking/triggerTable",
    searchParam,
    offset,
    pageSize
  );

export const cancelMarketmakingOrder = (id: number): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/deltaHedgeMarketMaking/cancel?OrderId=${id}`);

export const getMarketmakingConfig = (): Promise<IMarketmakingConfig> =>
  getRequest("/v1/deltaHedgeMarketMaking/GetConfig");

export const setMarketmakingConfig = (
  body: IMarketmakingConfig
): Promise<Packet<boolean>> =>
  putRequest("/v1/deltaHedgeMarketMaking/setConfig", body);

export const getMarketmakingProgram = (
  taskId: number
): Promise<IMarketmakingProgram> =>
  getRequest(`/v1/deltaHedgeMarketMaking/getProgram?taskId=${taskId}`);

export const setMarketmakingProgram = (
  body: IMarketmakingSetProgramBody
): Promise<Packet<boolean>> =>
  putRequest("/v1/deltaHedgeMarketMaking/setProgram", body);

export const setMarketmakingPredefinedProgram = (
  body: IMarketmakingPredefinedProgram
): Promise<Packet<boolean>> =>
  postRequest("/v1/deltaHedgeMarketMaking/AddPreDefinedProgram", body);

export const getMarketmakingPredefinedPrograms = (): Promise<
  IMarketmakingPredefinedProgram[]
> => getRequest("/v1/deltaHedgeMarketMaking/GetPreDefinedPrograms");

export const deleteMarketmakingPredefinedProgram = (
  name: string
): Promise<Packet<boolean>> =>
  deleteRequest(
    `/v1/deltaHedgeMarketMaking/DeletePreDefinedProgram?programName=${name}`
  );

export const getDistinctBaseInstrument = async (): Promise<
  Array<DropdownItem>
> => getRequest("/v1/deltaHedgeMarketMaking/DistinctBaseInstrument");

export const resumeMarketmakingTasks = (
  ids: number[]
): Promise<Packet<boolean>> =>
  patchRequest(`/v1/deltaHedgeMarketMaking/playTask`, { ids });

export const pauseMarketmakingTasks = (
  ids: number[]
): Promise<Packet<boolean>> =>
  patchRequest(`/v1/deltaHedgeMarketMaking/pauseTask`, { ids });

export const resendMarketmakingTasks = (
  ids: number[]
): Promise<Packet<boolean>> =>
  postRequest(`/v1/deltaHedgeMarketMaking/resendTask`, { ids });

export const deleteMarketmakingTasks = (
  ids: number[]
): Promise<Packet<boolean>> =>
  deleteRequest("/v1/deltaHedgeMarketMaking/deleteTask", { ids });

export const getPreviewOffers = (
  taskId: number
): Promise<IGetPreviewOfferResp> =>
  getRequest(`/v1/deltaHedgeMarketMaking/getPreviewOffers?taskId=${taskId}`);

export const setFirstRowDelta = (body: IFirstRowDelta) =>
  postRequest("/v1/deltaHedgeMarketMaking/setFirstRowDelta", body);

export const getManualConfig = (groupId: number): Promise<IManualConfigDTO> =>
  getRequest(`/v1/deltaHedgeMarketMaking/getManualConfig?groupId=${groupId}`);

export const setServerManualConfig = (
  dto: IManualConfigDTO
): Promise<Packet<boolean>> =>
  postRequest("/v1/deltaHedgeMarketMaking/setManualConfig", dto);
